import {regNavn} from 'felles/tjenester/tredjepartsTjenestenavn';
import {gettextCatalog} from 'angular-gettext';
import moment from "moment";

export class Datoformatering {

    static regNavn = "Datoformatering";
    static erAbsoluttDato = /^\d.*/;
    /** dersom periode.slutt er senere enn denne datoen antas sluttdato ikke å være satt. *
     * Merk at på java-siden er denne dato def i no.systemfabrikken.lederplan.delt.Dato.maxZonedDateTime (+33658-09-27T01:46:40Z),
     * men ved kjøring mot H2 database blir dette rundet av til år 2099 */
    static åpenSluttDatogrense = moment('2098-01-01T12:00:00Z');

    /** returnerer sann dersom angitt dato er større enn Datoformatering#åpenSluttDatogrense, eller null */
    static erÅpneSluttdato(sluttdato: Date): boolean {
        if (!sluttdato) return true;
        if (moment(sluttdato).isAfter(Datoformatering.åpenSluttDatogrense)) return true;
        return false;
    }

    /** returnerer angitt dato dersom denne er mindre enn Datoformatering#åpenSluttDatogrense,
     * eller null */
    static sluttdatoEllerNull(sluttdato:Date):Date|null{
        return Datoformatering.erÅpneSluttdato(sluttdato) ? null : sluttdato;
    }


    static forInjisering() {
        return [regNavn.gettext.gettextCatalog, Datoformatering]
    }

    constructor(private gettextCatalog: gettextCatalog) {
        this.registrerEgneFormat();
    }

    //offentlige metoder

    /**
     * Formatterer angitt periode i middels langt format med dagsnavn, dato og klokkeslett, f.els
     * ma. 13.03.2024 kl. 10:30 til 16:00
     * Støtter intervall som spenner videre til dag i fremtiden
     * Tekst blir da ma. 13.03.2024 kl. 10:30 til fredag 17.03.2024 kl. 16:00
     */
    somPeriodeMiddelsLang(start: Date, slutt: Date) {
        if (!start || !slutt) return "";

        let _start = moment(start),
          _slutt = moment(slutt);

        // Håndterer flerdagsmøte
        if(_slutt.day() > _start.day()) {
            return `${_start.format('ddd L LT')} ${this.gettextCatalog.getString('til')} ${_slutt.format('ddd L LT')}`
        }

        let formatert = _start.format('ddd L LT');

        //legg til slutttidspunkt, merk at vi foreløpig ikke tar høyde for intervall som spenner over midnatt
        formatert += ` ${this.gettextCatalog.getString('til')} ${_slutt.format("LT")}`;

        return formatert;
    }
    /**
     * Formaterer angitt periode, med klokkslett.
     * Benytter Moment.calendar for datoformatering,
     */
    somPeriode(start: Date, slutt: Date): string {
        if (!start || !slutt) return "";

        var _start = moment(start),
            _slutt = moment(slutt);
        var formatert = _start.calendar();

        //moment.calendar() viser ikke klokkeslett for absolutte datoer (kun for datoer på formen "i går", "i dag" etc)
        if (Datoformatering.erAbsoluttDato.test(formatert))
            formatert += " kl. " + _start.format("LT");

        //legg til slutttidspunkt, merk at vi foreløpig ikke tar høyde for intervall som spenner over midnatt
        formatert += ` ${this.gettextCatalog.getString('til')} ${_slutt.format("LT")}`;

        return formatert;
    }

    /**
     * Formaterer angitt periode, uten klokkeslett, som absolutte datoer (altså ingen bruk av relative angivelser som 'i går' etc).
     */
    somAbsoluttPeriodeUtenKlokkeslett(start: Date, slutt: Date): string {
        if (!start) return "";

        let formatert = `${moment(start).format('L')} ${this.gettextCatalog.getString('til')} `;
        formatert += Datoformatering.erÅpneSluttdato(slutt) ? this.gettextCatalog.getString('(slutt ikke angitt)') : moment(slutt).format('L');

        return formatert;
    }

    /**
     * Formaterer angitt periode, i langt format (med dagnavn og månedsnavn).
     * Benytter Moment.calendar for datoformatering,
     */
    somPeriodeLang(start: Date, slutt: Date): string {
        if (!start || !slutt) return "";

        var _start = moment(start),
            _slutt = moment(slutt);
        var formatert = _start.calendar();

        //moment.calendar() viser ikke klokkeslett for absolutte datoer (kun for datoer på formen "i går", "i dag" etc)
        if (Datoformatering.erAbsoluttDato.test(formatert))
            formatert = _start.format("LLLL");

        //legg til slutttidspunkt, merk at vi foreløpig ikke tar høyde for intervall som spenner over midnatt
        formatert += ` ${this.gettextCatalog.getString('til')} ${_slutt.format("LT")}`;

        return formatert;
    }


    /**
     * Formaterer angitt dato.
     * Benytter Moment.calendar for datoformatering, men tar alltid med klokkeslett
     */
    somKalenderMedKlokkeslett(dato: Date): string {
        if (!dato) return "";

        var _dato = moment(dato);
        var formatert = _dato.calendar();

        //moment.calendar() viser ikke klokkeslett for absolutte datoer (kun for datoer på formen "i går", "i dag" etc)
        if (Datoformatering.erAbsoluttDato.test(formatert))
            formatert += " " + _dato.format("LT");

        // bruker kode istedenfor oversettelse, siden disse er knyttet til hva som står i moment oversettelser.
        // se lederplan-klient/node_modules/moment/locale/en-gb.js
        // se lederplan-klient/node_modules/moment/locale/nb.js
        // se lederplan-klient/node_modules/moment/locale/nn.js
        var forrige = ""
        if (moment.locale() == 'nb'){
            forrige = 'forrige'
        }
        if (moment.locale() == 'nn'){
            forrige = 'Føregåande'
        }
        if (moment.locale() == 'en'){
            forrige = 'previous'
        }

        if (moment(_dato).isSame(moment(), 'week')) {
            return formatert.replace(`${forrige} `, "");
        }

        return formatert;
    }


    /**
     * Formaterer angitt dato.
     * Benytter Moment.calendar for datoformatering, men tar aldri med klokkeslett
     */
    somKalenderUtenKlokkeslett(dato: Date): string {
        if (!dato) return "";

        const orginal_locale = moment.locale();
        if (orginal_locale === "nb") moment.locale('nb_utenKlokkeslett');
        if (orginal_locale === "nn") moment.locale('nn_utenKlokkeslett');
        if (orginal_locale === "en") moment.locale('en_utenKlokkeslett');

        const formatert = moment(dato).calendar();

        moment.locale(orginal_locale);
        return formatert;
    }


    /**
     * Formaterer angitt dato.
     * Benytter Moment.fromNow for datoformatering, men tar aldri med klokkeslett
     */
    somFraNåUtenKlokkeslett(dato: Date): string {
        if (!dato) return "";

        //moment har en ikke helt logisk representasjon av antall dager,
        // håndterer derfor dette selv her. Når det er mer en 26 dager siden/til
        // aktuell dato overlater vi representasjonen til moment.
        var dato_ = moment(dato);
        var nå = moment();
        var erFør = dato_.isBefore(nå);
        erFør ? nå.endOf("day") : nå.startOf("day");
        var dagerTil = Math.abs(dato_.diff(nå, "days"));

        if (dagerTil === 0) return this.gettextCatalog.getString('i dag');

        if (dagerTil < 26) return erFør ?
            dagerTil < 2 ? this.gettextCatalog.getString('en dag siden') : `${dagerTil} ${this.gettextCatalog.getString('dager siden')}` :
            dagerTil < 2 ? this.gettextCatalog.getString('om en dag') : `${this.gettextCatalog.getString('om')} ${dagerTil} ${this.gettextCatalog.getString('dager')}`;

        return dato_.from(nå);
    }


    //privat

    private registrerEgneFormat() {
        const nb_utenKlokkeslett: any = {
            months: 'januar_februar_mars_april_mai_juni_juli_august_september_oktober_november_desember'.split('_'),
            monthsShort: 'jan._feb._mars_april_mai_juni_juli_aug._sep._okt._nov._des.'.split('_'),
            monthsParseExact: true,
            weekdays: 'søndag_mandag_tirsdag_onsdag_torsdag_fredag_lørdag'.split('_'),
            weekdaysShort: 'sø._ma._ti._on._to._fr._lø.'.split('_'),
            weekdaysMin: 'sø_ma_ti_on_to_fr_lø'.split('_'),
            weekdaysParseExact: true,
            longDateFormat: {
                LT: 'HH:mm',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D. MMMM YYYY',
                LLL: 'D. MMMM YYYY [kl.] HH:mm',
                LLLL: 'dddd D. MMMM YYYY [kl.] HH:mm'
            },
            calendar: {
                sameDay: '[i dag]', //opprinnelig: '[i dag kl.] LT'
                nextDay: '[i morgen]', //opprinnelig: '[i morgen kl.] LT'
                nextWeek: 'dddd', //opprinnelig: 'dddd [kl.] LT'
                lastDay: '[i går]', //opprinnelig: '[i går kl.] LT'
                lastWeek: '[forrige] dddd', //opprinnelig: '[forrige] dddd [kl.] LT'
                sameElse: 'L' //opprinnelig: 'L'
            },
            relativeTime: {
                future: 'om %s',
                past: '%s siden',
                s: 'noen sekunder',
                m: 'ett minutt',
                mm: '%d minutter',
                h: 'en time',
                hh: '%d timer',
                d: 'en dag',
                dd: '%d dager',
                M: 'en måned',
                MM: '%d måneder',
                y: 'ett år',
                yy: '%d år'
            },
            ordinalParse: /\d{1,2}\./,
            ordinal: '%d.',
            week: {
                dow: 1, // Monday is the first day of the week.
                doy: 4  // The week that contains Jan 4th is the first week of the year.
            }
        };

        const nn_utenKlokkeslett: any = {
            months: 'januar_februar_mars_april_mai_juni_juli_august_september_oktober_november_desember'.split('_'),
            monthsShort: 'jan._feb._mars_april_mai_juni_juli_aug._sep._okt._nov._des.'.split('_'),
            monthsParseExact: true,
            weekdays: 'sundag_måndag_tysdag_onsdag_torsdag_fredag_laurdag'.split('_'),
            weekdaysShort: 'su._må._ty._on._to._fr._lau.'.split('_'),
            weekdaysMin: 'su_må_ty_on_to_fr_la'.split('_'),
            weekdaysParseExact: true,
            longDateFormat: {
                LT: 'HH:mm',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D. MMMM YYYY',
                LLL: 'D. MMMM YYYY [kl.] HH:mm',
                LLLL: 'dddd D. MMMM YYYY [kl.] HH:mm'
            },
            calendar: {
                sameDay: '[i dag]', //opprinnelig: '[i dag kl.] LT'
                nextDay: '[i morgon]', //opprinnelig: '[i morgen kl.] LT'
                nextWeek: 'dddd', //opprinnelig: 'dddd [kl.] LT'
                lastDay: '[i går]', //opprinnelig: '[i går kl.] LT'
                lastWeek: '[føregåande] dddd', //opprinnelig: '[forrige] dddd [kl.] LT'
                sameElse: 'L' //opprinnelig: 'L'
            },
            relativeTime: {
                future: 'om %s',
                past: '%s sidan',
                s: 'nokre sekundar',
                m: 'eit minutt',
                mm: '%d minutt',
                h: 'ein time',
                hh: '%d timar',
                d: 'ein dag',
                dd: '%d dagar',
                M: 'ein månad',
                MM: '%d månadar',
                y: 'eit år',
                yy: '%d år'
            },
            ordinalParse: /\d{1,2}\./,
            ordinal: '%d.',
            week: {
                dow: 1, // Monday is the first day of the week.
                doy: 4  // The week that contains Jan 4th is the first week of the year.
            }
        };


        const en_utenKlokkeslett: any = {
            months: 'january_february_march_april_may_june_july_august_september_october_november_december'.split('_'),
            monthsShort: 'jan._feb._march_april_may_june_july_aug._sep._oct._nov._dec.'.split('_'),
            monthsParseExact: true,
            weekdays: 'sunday_monday_tuesday_wednesday_thursday_friday_saturday'.split('_'),
            weekdaysShort: 'sun._mo._tue._wd._thr._fri._sat.'.split('_'),
            weekdaysMin: 'su_ma_tu_we_th_fr_sa'.split('_'),
            weekdaysParseExact: true,
            longDateFormat: {
                LT: 'HH:mm',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D. MMMM YYYY',
                LLL: 'D. MMMM YYYY [kl.] HH:mm',
                LLLL: 'dddd D. MMMM YYYY [kl.] HH:mm'
            },
            calendar: {
                sameDay: '[today]', //opprinnelig: '[i dag kl.] LT'
                nextDay: '[tomorrow]', //opprinnelig: '[i morgen kl.] LT'
                nextWeek: 'dddd', //opprinnelig: 'dddd [kl.] LT'
                lastDay: '[yesterday]', //opprinnelig: '[i går kl.] LT'
                lastWeek: '[previous] dddd', //opprinnelig: '[forrige] dddd [kl.] LT'
                sameElse: 'L' //opprinnelig: 'L'
            },
            relativeTime: {
                future: 'in %s',
                past: '%s ago',
                s: 'some seconds',
                m: 'one minute',
                mm: '%d minutes',
                h: 'one hour',
                hh: '%d hours',
                d: 'one day',
                dd: '%d days',
                M: 'one month',
                MM: '%d months',
                y: 'one year',
                yy: '%d year'
            },
            ordinalParse: /\d{1,2}\./,
            ordinal: '%d.',
            week: {
                dow: 1, // Monday is the first day of the week.
                doy: 4  // The week that contains Jan 4th is the first week of the year.
            }
        };


        const aktivLocale = moment.locale();
        moment.locale('nb_utenKlokkeslett', nb_utenKlokkeslett);
        moment.locale('nn_utenKlokkeslett', nn_utenKlokkeslett);
        moment.locale('en_utenKlokkeslett', en_utenKlokkeslett);
        moment.locale(aktivLocale);
    }
}
